import React, { useRef, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import moment from 'moment';
import { Card, Button, Select, Alert } from 'antd';

import ReportCardTitle from '../components/shared/ReportCardTitle/ReportCardTitle';
import LoadingIndicator from '../components/shared/LoadingIndicator/LoadingIndicator';
import { useFetchLoadingTimeQuery } from './service';
import { useFetchMonitorRegionsQuery } from '../trackedWebsites/service';
import SiteSpeedChart from './SiteSpeedChart';
import useGetCurrentWebsite from '../deepAnalysis/websiteIndexEntry/hooks/useGetCurrentWebsite';
import { getSiteSpeedChartData } from './utils';
import './SiteSpeed.less';

const { Option } = Select;
type Interval = 'last_24_hours' | 'last_7_days' | 'last_30_days';

function getGroupBy(interval: Interval) {
    if (interval === 'last_24_hours') {
        return 'hour';
    }
    return 'day';
}

function getEndDate(interval: Interval) {
    const groupBy = getGroupBy(interval);
    const currentDate = moment().utc();
    let endDate = currentDate.format('YYYY-MM-DDT00:00:00');

    if (groupBy === 'hour') {
        endDate = currentDate.format('YYYY-MM-DDTHH:mm:ss');
    }

    return endDate;
}

function getStartDate(interval: Interval, endDateStr: string) {
    const endDate = moment(endDateStr);
    let startDate;

    if (interval === 'last_24_hours') {
        startDate = endDate.subtract(24, 'hours');
    } else if (interval === 'last_7_days') {
        startDate = endDate.subtract(7, 'days');
    } else {
        startDate = endDate.subtract(30, 'days');
    }

    return startDate.format('YYYY-MM-DDTHH:mm:ss');
}

export default function SiteSpeed() {
    const selectRegionsElement = useRef<Select<string>>(null);
    const { website: selectedWebsite } = useGetCurrentWebsite();
    const [selectedRegion, setSelectedRegion] = useState<string | undefined>(selectedWebsite?.settings.regions[0]);
    const [selectedInterval, setSelectedInterval] = useState<Interval>('last_24_hours');

    const endDateStr = getEndDate(selectedInterval);
    const startDateStr = getStartDate(selectedInterval, endDateStr);
    const groupBy = getGroupBy(selectedInterval);

    const { data: siteSpeedData, isFetching: isLoadingSiteSpeedData } = useFetchLoadingTimeQuery(selectedWebsite && selectedRegion ? {
        url: selectedWebsite.homepage_url,
        region: selectedRegion,
        interval: selectedInterval,
        startDate: startDateStr,
        endDate: endDateStr,
        groupBy: groupBy
    } : skipToken);

    const { data: monitorRegions } = useFetchMonitorRegionsQuery();

    const handleChangeRegion = (newRegion: string) => {
        setSelectedRegion(newRegion);
        if (selectRegionsElement.current) {
            selectRegionsElement.current.blur();
        }
    };

    const handleSetSelectedInterval = (newInterval: Interval) => {
        setSelectedInterval(newInterval);
    };

    if (!selectedWebsite) {
        return null;
    }

    return (
        <Card
            title={
                <ReportCardTitle
                    title={'Site Speed overview'}
                    domain={selectedWebsite.homepage_url}
                />
            }
            extra={
                <div className="regions-select">
                    <span>From region: </span>
                    <Select
                        className="monitor-locations"
                        style={{ width: 200 }}
                        defaultValue={selectedWebsite.settings.regions[0]}
                        onChange={handleChangeRegion}
                        ref={selectRegionsElement}
                    >
                        {monitorRegions &&
                            selectedWebsite.settings.regions.map((region) => (
                                <Option
                                    value={region}
                                    key={region}
                                >
                                    {monitorRegions.regions[region]}
                                </Option>
                            ))}
                    </Select>
                </div>
            }
            data-cy="site-speed"
        >
            {isLoadingSiteSpeedData || !siteSpeedData ? (
                <LoadingIndicator />
            ) : (
                <>
                    {siteSpeedData.reports.length === 0 ? (
                        <Alert message="No data for this url" />
                    ) : (
                        <SiteSpeedChart
                            chartData={getSiteSpeedChartData(siteSpeedData.reports)}
                            selectedInterval={selectedInterval}
                        />
                    )}

                    <div className="site-speed-buttons">
                        <Button
                            className={selectedInterval === 'last_24_hours' ? 'active' : ''}
                            onClick={() => handleSetSelectedInterval('last_24_hours')}
                        >
                            Last 24 hours
                        </Button>
                        <Button
                            className={selectedInterval === 'last_7_days' ? 'active' : ''}
                            onClick={() => handleSetSelectedInterval('last_7_days')}
                        >
                            Last 7 days
                        </Button>
                        <Button
                            className={selectedInterval === 'last_30_days' ? 'active' : ''}
                            onClick={() => handleSetSelectedInterval('last_30_days')}
                        >
                            Last 30 days
                        </Button>
                    </div>
                </>
            )}
        </Card>
    );
}
